import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import Swal from 'sweetalert2';
import Button from 'react-bootstrap/Button';

import { selectStatusByName, selectSectionAddClicksByName } from '@redux/selectors/FormSelector';
import { selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';

import DoorCard from './DoorCard';

const MAX = 2;
const whiteList = [
  'DoorDescription',
  'DoorHeight',
  'DoorType',
  'DoorWeight',
  'DoorWidth',
  'NumberDoors',
];
const SortableCard = SortableElement(props => (
  <DoorCard {...props} />
));

const Grid = SortableContainer(({
  fileList,
  fieldsList,
  validateFigField,
  something,
  ...otherProps
}) => (
  <div className={`grid grid-${MAX}-col`}>
    {fileList.map(([k, file], index) => (
      (!file.IsDeleted)
      && (
        <SortableCard
          key={k} // consumed by react-sortable-hoc
          index={index} // consumed by react-sortable-hoc
          sortIndex={index}
          file={file}
          validateFigField={validateFigField}
          fieldsList={fieldsList}
          {...otherProps}
        />
      )
    ))}
  </div>
));

const RepeatableDoors = ({ fields, validateFigField, doorsPayload }) => {
  const { values, setValues, setFieldValue } = useFormikContext();
  const statusDoors = useSelector(state => selectStatusByName(state, 'Doors'));
  const addClickCounter = useSelector(state => selectSectionAddClicksByName(state, 'Doors'));
  const maxCount = useMemo(() => ((fields[0].repeatableMaxCount) ? fields[0].repeatableMaxCount : 25), [fields]);

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const doorsCount = sectionsPerRepeatableSection.Doors.length;

  const fileList = useMemo(() => {
    const array = [];
    if (values.Doors) {
      Object.entries(values.Doors).forEach(([k, file]) => {
        if (!file.IsDeleted) {
          array.push([k, file]);
        }
      });
    }
    return array;
  }, [values?.Doors]);

  const fieldsList = useMemo(() => {
    const array = [];
    fields.forEach(field => {
      if (whiteList.includes(field.FieldName)) {
        array.push(field);
      }
    });
    return array;
  }, [fields]);

  const buildFormikValues = () => {
    const newValues = {};
    let index = 0;
    doorsPayload.sort((a, b) => a.SortOrder - b.SortOrder).forEach(entry => {
      const uid = uuidv4(); // keep this an unique string that cannot be converted to a number (prevents keys reordering)
      newValues[uid] = {
        uid,
        UniqueID: entry.UniqueID,
        DoorDescription: entry.DoorDescription,
        DoorHeight: entry.DoorHeight,
        DoorType: entry.DoorType,
        DoorWeight: entry.DoorWeight,
        DoorWidth: entry.DoorWidth,
        NumberDoors: entry.NumberDoors,
        SortOrder: entry.SortOrder,
        IsDeleted: entry.IsDeleted,
        IsLive: entry.IsLive,
      };
      // ensure correct sort order
      if (!entry.IsDeleted) {
        newValues[uid].SortOrder = index + 1;
        index += 1;
      }
    });
    return newValues;
  };

  useEffect(() => {
    if (doorsPayload?.length) {
      const newValues = buildFormikValues();
      setFieldValue('Doors', {
        ...values.Doors,
        ...newValues,
      });
    }
  }, [doorsPayload]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Doors Exceeded',
    html: `The maximum number of doors must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const addDoor = () => {
    if (doorsCount + 1 > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    const uid = uuidv4();
    setValues({
      ...values,
      Doors: {
        ...values.Doors,
        [uid]: {
          uid,
          DoorDescription: '',
          DoorHeight: '',
          DoorType: '',
          DoorWeight: '',
          DoorWidth: '',
          NumberDoors: '',
          SortOrder: fileList.length + 1, // fileList excludes deleted entries
          IsDeleted: false,
          IsLive: false,
        },
      },
    });
  };

  // after sort ends derive new order, then set formik state
  const onSortEnd = ({ oldIndex, newIndex }) => {
    const sortedArray = arrayMoveImmutable(fileList, oldIndex, newIndex);
    let newValues = {};
    sortedArray.forEach((file, index) => {
      newValues = {
        ...newValues,
        [file[0]]: {
          ...file[1],
          SortOrder: index + 1,
        },
      };
    });
    // preserve deleted entries - excluded from fileList
    Object.entries(values.Doors).forEach(file => {
      if (file[1].IsDeleted) {
        newValues = {
          ...newValues,
          [file[0]]: {
            ...file[1],
          },
        };
      }
    });
    setValues({
      ...values,
      Doors: { ...newValues },
    });
  };

  useEffect(() => {
    if (addClickCounter) {
      addDoor();
    }
  }, [addClickCounter]);

  return (
    <>
      { statusDoors?.errorMessage && <div className="invalid-feedback">{statusDoors.errorMessage}</div> }
      <Button
        name="btnAddDoor"
        className="float-right"
        onClick={addDoor}
        size="sm"
      >
        <i className="fas fa-plus mr-2" />
        Add Door
      </Button>
      <br className="clearfix" />
      <Grid
        axis="xy"
        distance={20} // set distance to prevent click events from being swallowed
        fileList={fileList}
        fieldsList={fieldsList}
        validateFigField={validateFigField}
        onSortEnd={onSortEnd}
        useDragHandle
      />
      <br className="clearfix" />
      {(doorsCount > MAX) && (
        <>
          <Button
            name="btnAddDoor"
            className="float-right"
            onClick={addDoor}
            size="sm"
          >
            <i className="fas fa-plus mr-2" />
            Add Door
          </Button>
          <br className="clearfix" />
        </>
      )}
    </>
  );
};

RepeatableDoors.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape()),
  validateFigField: PropTypes.func,
  doorsPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default RepeatableDoors;
